import React, { useState, useEffect } from 'react';
import AdminDashboard from './AdminDashboard';

const AdminPage = ({ currentUserId }) => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await fetch('/api/v1/users/me', {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();

      if (response.ok) {
        setUserRoles(data.data.user.roles || []);
        
        // Vérifier si l'utilisateur a des droits admin
        const hasAdminRights = data.data.user.roles?.includes('admin') || 
                              data.data.user.roles?.includes('bureau') ||
                              data.data.user.level === 'membre_fondateur';
        
        if (!hasAdminRights) {
          setError('Accès non autorisé. Vous devez avoir des droits d\'administration.');
        }
      } else {
        setError('Erreur lors de la vérification des droits');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const navigation = [
    {
      id: 'dashboard',
      label: 'Tableau de Bord',
      icon: 'fas fa-tachometer-alt',
      description: 'Vue d\'ensemble des statistiques'
    },
    {
      id: 'sponsorships',
      label: 'Parrainages',
      icon: 'fas fa-users',
      description: 'Gestion des demandes de parrainage'
    },
    {
      id: 'users',
      label: 'Utilisateurs',
      icon: 'fas fa-user-friends',
      description: 'Gestion des membres'
    },
    {
      id: 'roles',
      label: 'Rôles & Permissions',
      icon: 'fas fa-shield-alt',
      description: 'Attribution des rôles'
    },
    {
      id: 'settings',
      label: 'Paramètres',
      icon: 'fas fa-cog',
      description: 'Configuration du système'
    }
  ];

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Vérification des droits...</span>
          </div>
          <p className="mt-3 text-muted">Vérification des droits d'accès...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center">
                <div className="mb-4">
                  <i className="fas fa-shield-alt fa-3x text-danger"></i>
                </div>
                <h4 className="text-danger">Accès Refusé</h4>
                <p className="text-muted">{error}</p>
                <div className="mt-4">
                  <a href="/" className="btn btn-primary me-2">
                    Retour à l'accueil
                  </a>
                  <a href="/contact" className="btn btn-outline-secondary">
                    Demander l'accès
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        {/* Sidebar Navigation */}
        <div className="col-md-3 col-lg-2 bg-light min-vh-100 p-0">
          <div className="position-sticky top-0">
            {/* Header */}
            <div 
              className="p-3 text-white text-center"
              style={{ backgroundColor: '#1E4F8A' }}
            >
              <h5 className="mb-0">
                <i className="fas fa-shield-alt me-2"></i>
                Administration
              </h5>
            </div>

            {/* User Info */}
            <div className="p-3 border-bottom">
              <div className="text-center">
                <div className="mb-2">
                  <i className="fas fa-user-circle fa-2x" style={{ color: '#00256C' }}></i>
                </div>
                <h6 className="mb-1" style={{ color: '#00256C' }}>
                  Administrateur
                </h6>
                <div>
                  {userRoles.map(role => (
                    <span key={role} className="badge bg-secondary me-1">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="p-2">
              {navigation.map(item => (
                <button
                  key={item.id}
                  className={`btn btn-light w-100 text-start mb-2 ${
                    activeSection === item.id ? 'active' : ''
                  }`}
                  onClick={() => setActiveSection(item.id)}
                  style={{
                    backgroundColor: activeSection === item.id ? '#1E4F8A' : 'transparent',
                    color: activeSection === item.id ? 'white' : '#00256C',
                    border: 'none'
                  }}
                >
                  <i className={`${item.icon} me-2`}></i>
                  <div>
                    <div className="fw-bold">{item.label}</div>
                    <small className="opacity-75">{item.description}</small>
                  </div>
                </button>
              ))}
            </nav>

            {/* Quick Links */}
            <div className="p-3 border-top">
              <h6 className="text-muted mb-2">Accès Rapide</h6>
              <div className="d-grid gap-1">
                <a href="/sponsorships/new" className="btn btn-sm btn-outline-primary">
                  <i className="fas fa-plus me-1"></i>
                  Nouveau Parrainage
                </a>
                <a href="/sponsorships" className="btn btn-sm btn-outline-secondary">
                  <i className="fas fa-list me-1"></i>
                  Voir Parrainages
                </a>
                <a href="/" className="btn btn-sm btn-outline-info">
                  <i className="fas fa-home me-1"></i>
                  Site Principal
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="col-md-9 col-lg-10">
          <div className="p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 style={{ color: '#00256C' }}>
                  {navigation.find(item => item.id === activeSection)?.label}
                </h2>
                <p className="text-muted mb-0">
                  {navigation.find(item => item.id === activeSection)?.description}
                </p>
              </div>
              <div>
                <button 
                  className="btn btn-outline-primary me-2"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-sync-alt me-1"></i>
                  Actualiser
                </button>
                <a href="/" className="btn btn-outline-secondary">
                  <i className="fas fa-times me-1"></i>
                  Quitter Admin
                </a>
              </div>
            </div>

            {/* Content */}
            <div className="content-area">
              {activeSection === 'dashboard' && (
                <AdminDashboard />
              )}

              {activeSection === 'sponsorships' && (
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Gestion des Parrainages</h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted">
                      Interface de gestion détaillée des parrainages.
                    </p>
                    <div className="d-grid gap-2 d-md-flex">
                      <a href="/admin/sponsorships" className="btn btn-primary">
                        <i className="fas fa-list me-2"></i>
                        Voir tous les parrainages
                      </a>
                      <a href="/admin/sponsorships/pending" className="btn btn-warning">
                        <i className="fas fa-clock me-2"></i>
                        Parrainages en attente
                      </a>
                      <a href="/admin/reports/sponsorships" className="btn btn-info">
                        <i className="fas fa-chart-bar me-2"></i>
                        Rapports
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'users' && (
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Gestion des Utilisateurs</h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted">
                      Interface de gestion des membres de l'association.
                    </p>
                    <div className="d-grid gap-2 d-md-flex">
                      <a href="/admin/users" className="btn btn-primary">
                        <i className="fas fa-users me-2"></i>
                        Voir tous les utilisateurs
                      </a>
                      <a href="/admin/users/new" className="btn btn-success">
                        <i className="fas fa-user-plus me-2"></i>
                        Ajouter un utilisateur
                      </a>
                      <a href="/admin/users/export" className="btn btn-info">
                        <i className="fas fa-download me-2"></i>
                        Exporter la liste
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'roles' && (
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Gestion des Rôles et Permissions</h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted">
                      Attribution et gestion des rôles des membres.
                    </p>
                    <div className="d-grid gap-2 d-md-flex">
                      <a href="/admin/roles" className="btn btn-primary">
                        <i className="fas fa-shield-alt me-2"></i>
                        Gérer les rôles
                      </a>
                      <a href="/admin/permissions" className="btn btn-warning">
                        <i className="fas fa-key me-2"></i>
                        Permissions
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'settings' && (
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Paramètres du Système</h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted">
                      Configuration générale de l'application.
                    </p>
                    <div className="d-grid gap-2 d-md-flex">
                      <a href="/admin/settings/general" className="btn btn-primary">
                        <i className="fas fa-cog me-2"></i>
                        Paramètres généraux
                      </a>
                      <a href="/admin/settings/email" className="btn btn-info">
                        <i className="fas fa-envelope me-2"></i>
                        Configuration email
                      </a>
                      <a href="/admin/settings/security" className="btn btn-warning">
                        <i className="fas fa-lock me-2"></i>
                        Sécurité
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;